import { Component, OnInit, ViewChild, ElementRef, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CourseService } from '../../../services/course.service';
import { WebSocketService, WebSocketMessage } from '../../../services/websocket.service';
import { AuthStateService } from '../../../core/states/auth-state.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { PrivateMessage } from '../../../models/PrivateMessage';
import { PrivateConversation } from '../../../models/PrivateConversation';

@Component({
  selector: 'app-messages',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './messages.component.html',
  styleUrl: './messages.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'class': 'block h-full overflow-hidden' }
})
export class MessagesComponent implements OnInit, OnDestroy {

  @ViewChild('messageContainer') messageContainer!: ElementRef<HTMLDivElement>;

  conversations: PrivateConversation[] = [];
  selectedConversation: PrivateConversation | null = null;
  messages: PrivateMessage[] = [];
  newMessageText = '';
  showChatModal = false;
  typingUser: { userId: number; userName: string } | null = null;

  private currentChatId: number | null = null;
  private currentUserId = 0;
  private currentUserRoles: string[] = [];
  private typingTimeout?: ReturnType<typeof setTimeout>;
  private typingSubject = new Subject<boolean>();
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private courseService: CourseService,
    private wsService: WebSocketService,
    private authService: AuthStateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initUser();
    this.initTypingDebounce();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearTypingTimeout();
    if (this.currentChatId) {
      this.wsService.unsubscribeFromChat(this.currentChatId);
    }
  }

  // ============ INICIALIZACIÓN ============

  private initUser(): void {
    this.authService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user?.profile?.id) {
          this.currentUserId = user.profile.id;
          this.currentUserRoles = user.roles || [];
          this.loadConversations();
          this.setupWebSocket();
        }
      });
  }

  private initTypingDebounce(): void {
    this.typingSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(isTyping => {
      if (this.currentChatId) {
        this.courseService.notifyTyping(this.currentChatId, isTyping).subscribe();
      }
    });
  }

  private setupWebSocket(): void {
    this.wsService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(msg => this.handleWsMessage(msg));
  }

  // ============ WEBSOCKET HANDLERS ============

  private handleWsMessage(wsMessage: WebSocketMessage): void {
    if (wsMessage.type === 'new_message') {
      this.onNewMessage(wsMessage.data);
    } else if (wsMessage.type === 'typing') {
      this.onTyping(wsMessage.data);
    }
  }

  private onNewMessage(data: any): void {
    // Actualizar conversación en la lista
    const conv = this.conversations.find(c => c.id === data.private_chat_id);
    if (conv) {
      conv.last_message = data.message;
      conv.last_message_date = data.created_at;
      
      // Reordenar conversaciones por fecha (más recientes primero)
      this.conversations.sort((a, b) => {
        const dateA = new Date(a.last_message_date || 0).getTime();
        const dateB = new Date(b.last_message_date || 0).getTime();
        return dateB - dateA;
      });
    }

    // Agregar mensaje si es del chat actual y no es propio
    if (data.private_chat_id === this.currentChatId && data.sender_id !== this.currentUserId) {
      this.messages = [...this.messages, this.mapMessage(data)];
      this.typingUser = null;
      this.scrollToBottom();
    }
    this.cdr.markForCheck();
  }

  private onTyping(data: any): void {
    if (data.chat_id !== this.currentChatId || data.user_id === this.currentUserId) return;

    if (data.is_typing) {
      const wasNearBottom = this.isNearBottom();
      this.typingUser = { userId: data.user_id, userName: data.user_name };
      if (wasNearBottom) this.scrollToBottom();
      this.resetTypingTimeout(() => this.typingUser = null, 3000);
    } else {
      this.typingUser = null;
    }
    this.cdr.markForCheck();
  }

  // ============ CONVERSACIONES ============

  loadConversations(): void {
    this.courseService.getPrivateConversations(0).subscribe({
      next: (res) => {
        this.conversations = Array.isArray(res) ? res.map(c => this.mapConversation(c)) : [];
        
        // Ordenar por fecha del último mensaje (más recientes primero)
        this.conversations.sort((a, b) => {
          const dateA = new Date(a.last_message_date || 0).getTime();
          const dateB = new Date(b.last_message_date || 0).getTime();
          return dateB - dateA;
        });
        
        this.cdr.markForCheck();
        
        // Verificar si hay un chatId en los query params para abrirlo automáticamente
        this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
          if (params['chatId']) {
            const chatId = +params['chatId'];
            const conversation = this.conversations.find(c => c.id === chatId);
            if (conversation) {
              this.openChat(conversation);
            }
          }
        });
      },
      error: () => {
        this.conversations = [];
        this.cdr.markForCheck();
      }
    });
  }

  private mapConversation(chat: any): PrivateConversation {
    const lastMsg = chat.messages?.at(-1);
    return {
      id: chat.id,
      student_id: chat.student_id,
      teacher_id: chat.teacher_id,
      student_name: chat.student?.name || `Estudiante ${chat.student_id}`,
      teacher_name: chat.teacher?.name || `Profesor ${chat.teacher_id}`,
      course_name: '',
      last_message: lastMsg?.message || '',
      last_message_date: lastMsg?.created_at || chat.created_at || '',
      unread_count: chat.unread_count || 0
    };
  }

  openChat(conversation: PrivateConversation): void {
    if (this.currentChatId) {
      this.wsService.unsubscribeFromChat(this.currentChatId);
    }

    this.selectedConversation = conversation;
    this.currentChatId = conversation.id;
    this.showChatModal = true;
    this.typingUser = null;

    this.loadMessages(conversation.id);
    this.wsService.subscribeToChat(conversation.id);

    // Marcar mensajes como leídos
    this.markConversationAsRead(conversation.id);
  }

  markConversationAsRead(chatId: number): void {
    this.courseService.markMessagesAsRead(chatId).subscribe({
      next: (response) => {
        if (response.success) {
          // Actualizar contador en la conversación
          const conv = this.conversations.find(c => c.id === chatId);
          if (conv) {
            conv.unread_count = 0;
          }
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Error marking messages as read:', error);
      }
    });
  }

  closeChat(): void {
    if (this.currentChatId) {
      this.wsService.unsubscribeFromChat(this.currentChatId);
    }
    this.showChatModal = false;
    this.selectedConversation = null;
    this.messages = [];
    this.newMessageText = '';
    this.currentChatId = null;
    this.typingUser = null;
    this.cdr.markForCheck();
  }

  deleteConversation(conversation: PrivateConversation, event: MouseEvent): void {
    event.stopPropagation();
    this.courseService.deletePrivateConversation(conversation.id).subscribe({
      next: () => {
        this.conversations = this.conversations.filter(c => c.id !== conversation.id);
        if (this.selectedConversation?.id === conversation.id) {
          this.closeChat();
        }
        this.cdr.markForCheck();
      }
    });
  }

  // ============ MENSAJES ============

  private loadMessages(chatId: number): void {
    this.courseService.getPrivateMessages(chatId).subscribe({
      next: (msgs: any[]) => {
        this.messages = msgs.map(m => this.mapMessage(m));
        this.scrollToBottom();
        this.cdr.markForCheck();
      },
      error: () => {
        this.messages = [];
        this.cdr.markForCheck();
      }
    });
  }

  private mapMessage(msg: any): PrivateMessage {
    return { ...msg, senderId: msg.sender_id, timestamp: msg.created_at };
  }

  sendMessage(): void {
    const text = this.newMessageText.trim();
    if (!this.currentChatId || !text) return;

    this.typingSubject.next(false);
    this.newMessageText = '';

    this.courseService.sendPrivateMessage(this.currentChatId, text).subscribe({
      next: (msg) => this.addMessage(msg),
      error: () => this.addMessage({
        id: Date.now(),
        message: text,
        sender_id: this.currentUserId,
        created_at: new Date().toISOString()
      })
    });
  }

  private addMessage(msg: any): void {
    this.messages = [...this.messages, this.mapMessage(msg)];
    if (this.selectedConversation) {
      this.selectedConversation.last_message = msg.message;
      this.selectedConversation.last_message_date = msg.created_at;
      
      // Reordenar conversaciones por fecha (más recientes primero)
      this.conversations.sort((a, b) => {
        const dateA = new Date(a.last_message_date || 0).getTime();
        const dateB = new Date(b.last_message_date || 0).getTime();
        return dateB - dateA;
      });
    }
    this.scrollToBottom();
    this.cdr.markForCheck();
  }

  onInputChange(): void {
    this.typingSubject.next(true);
    this.resetTypingTimeout(() => this.typingSubject.next(false), 1000);
  }

  // ============ UTILIDADES ============

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messageContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }

  private isNearBottom(): boolean {
    const el = this.messageContainer?.nativeElement;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  }

  private resetTypingTimeout(callback: () => void, delay: number): void {
    this.clearTypingTimeout();
    this.typingTimeout = setTimeout(() => {
      callback();
      this.cdr.markForCheck();
    }, delay);
  }

  private clearTypingTimeout(): void {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = undefined;
    }
  }

  // ============ TEMPLATE HELPERS ============

  getChatHeaderName(): string {
    if (!this.selectedConversation) return '';
    return this.selectedConversation.teacher_id === this.currentUserId
      ? this.selectedConversation.student_name
      : this.selectedConversation.teacher_name;
  }

  getOtherPersonName(conv: PrivateConversation): string {
    return conv.teacher_id === this.currentUserId ? conv.student_name : conv.teacher_name;
  }

  isMyMessage(msg: PrivateMessage): boolean {
    return msg.senderId === this.currentUserId;
  }

  canSendMessages(): boolean {
    return !this.currentUserRoles.includes('student');
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const diffHours = (Date.now() - date.getTime()) / 3600000;
    return diffHours < 24
      ? date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
  }

  trackByConversation(_: number, conv: PrivateConversation): number {
    return conv.id;
  }

  trackByMessage(_: number, msg: PrivateMessage): number {
    return msg.id!;
  }
}
